import { Invoice, InvoiceLine, ServiceTransaction } from '../types';
import {
    AllocatableEntry,
    OpenEntry,
    allocatableEntries,
    allocatedByEntry,
    openEntries,
    planAllocations,
} from './allocationLogic';
import { loggedQuantityInPeriod } from './invoiceLogic';
import { ServiceJournalRow, isJournalMappable, journalToServiceTransactions } from './serviceJournal';
import { asDay, buchungstag } from './serviceLogUtils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DER SCHATTENVERGLEICH DER ZUORDNUNG (vor S5a)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * S5a stellt `allocations.entryId` — welche erbrachte Menge welchem Beleg und
 * damit welchem Auftraggeber berechnet ist — vom Projekt-JSON auf
 * `public.service_journal` um. Das ist der einzige Schritt des Vorhabens, der
 * sich nur mit einer Gutschrift zurueckholen laesst: eine im Journal fehlende
 * Zeile fordert zu wenig, eine zusaetzliche fordert zu viel, und beides faellt
 * erst bei der naechsten kumulativen Rechnung auf — Wochen spaeter, wenn der
 * Beleg beim Kunden liegt.
 *
 * Deshalb wird VORHER gemessen: dieselbe Zuordnung zweimal gerechnet, einmal
 * aus dem JSON und einmal aus dem Journal, und die Unterschiede werden benannt.
 * Dieses Modul stellt nichts um und wird von keinem Schreibpfad benutzt.
 *
 * ─── Warum hier NICHTS nachgebaut wird ─────────────────────────────────────
 * Beide Wege laufen durch dieselben Funktionen aus allocationLogic —
 * `allocatableEntries`, `openEntries`, `planAllocations`. Der einzige
 * Unterschied zwischen ihnen ist das Protokoll, das hineingeht. Eine
 * nachgebaute Zuordnungsrechnung maesse am Ende sich selbst: sie wuerde
 * beweisen, dass die NACHBILDUNG auf beiden Protokollen dasselbe tut, und
 * genau die Frage ist uninteressant. Gemessen werden muss der Ernstfall.
 *
 * Auch der Journal-Weg macht deshalb nichts Eigenes, sondern genau das, was
 * `getServiceLog` spaeter tut: `journalToServiceTransactions(journal)` und dann
 * dieselben Funktionen. Zurueckgenommene Zeilen (`voided_at`) filtert die
 * Rueckabbildung bereits selbst; hier wird das nicht wiederholt, sondern nur
 * GEZAEHLT (siehe `gefiltert.zurueckgenommen`).
 *
 * ─── Seit S5b entscheiden beide Wege VERSCHIEDEN ueber dieselbe Zeile ──────
 * Der Journal-Weg liest die Buchungsart aus der Spalte (`service_journal.kind`,
 * von `journalToServiceTransactions` mitgegeben), der JSON-Weg erschliesst sie
 * weiter aus Praefix und Text — `isPostingEntry` kann beides. Die einzige
 * bekannte Abweichungsklasse ist die Zeile mit `ALT-`-Referenz UND dem Text
 * „manuelle Verbuchung": der Rueckfall nennt sie Buchung, das Journal
 * `legacy_backfill` und damit keine. Auf PROD ist diese Klasse zum 18.09.2026
 * leer; taucht sie auf, ist der Befund hier ECHT und kein Rauschen.
 *
 * Dass der JSON-Weg wirklich aus Praefix und Text entscheidet, ist seit dem
 * Buchungsart-Register (S5b) eine BEDINGUNG und keine Selbstverstaendlichkeit:
 * ist das Register geladen, liest `isPostingEntry` auch fuer eine JSON-Zeile
 * die Spalte. Deshalb leert der Nachtlauf das Register vor dem Vergleich
 * (scripts/integrity-sweep.ts). Ohne das waeren beide Seiten immer einig, und
 * diese Messung bestaetigte nur noch sich selbst.
 *
 * ─── Was eine interessante Abweichung ist ──────────────────────────────────
 * NICHT "ein Feld ist anders". Die Zuordnung liest aus einer Protokollzeile
 * nur `id`, `serviceId`, `quantity`, `date` und — ueber `isPostingEntry` —
 * `kind` beziehungsweise `bookingReference`/`description`. Ein Unterschied in `customerId`,
 * `splitPartyId`, `bookedAt` oder `performerName` aendert an der Zuordnung
 * nichts und erzeugt hier von Bauart wegen keinen Befund. Umgekehrt: aendert
 * sich `bookingReference` so, dass die Zeile zur BUCHUNG wird, faellt sie aus
 * der Abrechenbarkeit — und das erscheint als fehlender Eintrag, weil es
 * genau das ist.
 *
 * Die Identitaet ist deshalb das PAAR (serviceId, entryId) und nicht die id
 * allein: `moveAltBackfill` schreibt die `serviceId` bestehender Zeilen um
 * (bekannte Abweichung 2 in serviceJournal.ts). Steht die Zeile im Journal
 * noch an der alten Leistung, fehlt sie an der neuen und ist an der alten zu
 * viel — zwei Befunde, und beide sind wahr. Ueber die id allein gerechnet
 * saehe man nur "Menge gleich" und uebersaehe, dass die Leistung an einer
 * anderen LV-Position landet.
 *
 * ─── Die Reihenfolge, ohne Alarm bei belangloser Sortierstabilitaet ────────
 * `orderForPeriod` sortiert je Leistung nach (im Zeitraum?, Datum) und ist
 * eine STABILE Sortierung: bei gleichem Datum entscheidet die Position im
 * Array, und die ist zwischen JSON (viele Pfade haengen vorn an) und Journal
 * (`loadJournal` sortiert nach `entry_at`) systematisch verschieden. Ein
 * Vergleich der rohen Reihenfolge meldete deshalb bei fast jedem Projekt einen
 * Unterschied, der keiner ist: welche von zwei Begehungen desselben Tages
 * zuerst gezogen wird, aendert weder Menge noch Geld.
 *
 * Verglichen wird darum die Reihenfolge in GRUPPEN gleichen Sortierschluessels:
 * Reihenfolge der Gruppen und Inhalt jeder Gruppe als Menge. Was innerhalb
 * einer Gruppe steht, ist Unentschieden und wird nicht bewertet. Der
 * Gruppenschluessel ist ohne Zeitraum das rohe Datum der Zeile; einzig die
 * doppelte Herbststunde gilt als EIN Unentschieden (`gruppenschluessel`, V3).
 * Wer den Vergleicher in `orderForPeriod` aendert, muss diese Gruppierung
 * mitaendern, sonst wird sie entweder wieder laut oder — die schlechtere
 * Richtung — blind fuer eine echte Umsortierung.
 *
 * Seit dem 03.10.2026 sortiert `orderForPeriod` nicht mehr nach dem Rohtext,
 * sondern nach der Berliner Wanduhr (`berlinerSortierzeit`) und bei gleichem
 * Zeitpunkt nach der id (Codex-Review S5a, P2). Die Gruppierung bleibt beim
 * rohen Schluessel, und das ist geprueft: gleicher Rohtext heisst gleiche
 * Wanduhr, eine Gruppe ist also nie GROEBER als der neue Gleichstand — blind
 * wird sie nicht. Feiner kann sie sein (zwei Schreibweisen derselben
 * Sekunde), das war mit dem Rohtext-Vergleicher genauso. Weggefallen sind
 * nur Meldungen, die allein aus der Schreibweise kamen: ein reiner Tag gegen
 * einen UTC-Zeitstempel desselben Berliner Tages ordnet jetzt auf beiden
 * Wegen gleich.
 *
 * Der Vergleich laeuft OHNE Zeitraum. Einen Zeitraum gibt es erst, wenn ein
 * Beleg ausgestellt wird; ihn hier zu erfinden hiesse, eine Annahme zu messen.
 * Ohne Zeitraum liegt alles in derselben Gruppe "im Zeitraum", und uebrig
 * bleibt die Datumsordnung — die Ordnung, die in jedem Beleg die Grundlage ist.
 *
 * ─── Die Zeitraeume der AUSGESTELLTEN Belege (S5a Stufe 2) ─────────────────
 * Erfunden wird weiterhin keiner. Wer `perioden` mitgibt (der Nachtlauf: die
 * Leistungszeitraeume der ausgestellten Belege des Projekts), bekommt je
 * Zeitraum und Leistung die protokollierte Menge beider Seiten verglichen —
 * `loggedQuantityInPeriod` selbst, der Tag BEIDSEITIG der Berliner Tag
 * (`zeitraumMengen`, Entscheid 1). Neue Art `zeitraum_menge_weicht_ab`.
 *
 * Gemessen wird die BRUTTOMENGE des Zeitraums, ohne die Belege abzuziehen:
 * mit ihnen zoege jeder ausgestellte Beleg seinen eigenen Zeitraum leer, und
 * der Vergleich laese an genau den Stellen 0 gegen 0, auf die er schauen
 * soll. Was offen ist, vergleicht oben bereits `offene_menge_weicht_ab` je
 * Eintrag. Neu sieht dieser Teil, was die Eintragsvergleiche nicht sehen:
 * einen BEREITS ABGERECHNETEN Eintrag, der auf beiden Seiten steht, aber an
 * einem anderen Tag (der Reihenfolgevergleich kennt nur offene Eintraege),
 * und Korrekturen nach unten, die nie abrechenbar sind und deshalb in keinem
 * Eintragsvergleich vorkommen, die Menge des Zeitraums aber mindern. Fehlt
 * ein Eintrag ganz, steht die Zeitraummenge ZUSAETZLICH zum Eintragsbefund
 * da — sie nennt dann, welche Belegzeitraeume er beruehrt.
 *
 * ─── Was als ERWARTET herausgefiltert wird, und was nicht ──────────────────
 * Aus der Liste "BEKANNTE ABWEICHUNGEN" im Kopf von serviceJournal.ts:
 *
 * 1. ZURUECKGENOMMENE ZEILEN — gefiltert, und zwar von
 *    `journalToServiceTransactions` selbst. Sie sind gewollt aus dem Protokoll
 *    heraus; sie hier zu melden hiesse, die Ruecknahme als Fehler auszugeben.
 *    Gezaehlt in `gefiltert.zurueckgenommen`.
 * 2. GEAENDERTE ZEILEN — NICHT gefiltert. Genau hier steckt das Geld: eine im
 *    Journal stehengebliebene alte `quantity` oder `serviceId` ist die
 *    6105-Klasse (Zuordnungsumzug), und dass die Ursache bekannt ist, macht
 *    die falsche Zuordnung nicht harmlos. Ein Filter hier machte die Messung
 *    wertlos.
 * 3. VERLORENE VORMERKUNGEN — NICHT gefiltert. Eine JSON-Zeile ohne
 *    Journalzeile fordert auf dem Journal-Weg zu wenig; die Ursache ist
 *    bekannt, die Folge waere Geld.
 *
 * Dazu eine vierte Klasse, die nicht in dieser Liste steht und trotzdem
 * systematisch ist: NICHT ABBILDBARE Protokollzeilen (`isJournalMappable` —
 * keine Leistung, keine lesbare Menge, kein ableitbarer Kalendertag). Sie
 * koennen im Journal NIE stehen; Migration Nr. 60 legt sie in die Quarantaene.
 * Sie einzeln zu melden hiesse, bei jedem Projekt mit undatiertem Altbestand
 * eine Liste zu erzeugen, in der die echten Befunde untergehen — deshalb
 * gefiltert und in `gefiltert.nichtAbbildbar` GEZAEHLT. Stumm ist das nicht:
 * die Zahl steht im Ergebnis, sie geht in `describeShadowResult` mit ein, und
 * solange sie ueber null liegt, ist `gleich` FALSCH. Der Weg fuer diese Zeilen
 * ist nicht die Zuordnung, sondern die Quarantaene — beantwortet werden muss
 * er trotzdem, bevor S5a laeuft.
 *
 * ─── Wo der Vergleich blind bleibt ─────────────────────────────────────────
 * - Eine JSON-Zeile OHNE id kennt der JSON-Weg gar nicht
 *   (`allocatableEntries` verlangt eine id), das Journal gibt ihr einen
 *   abgeleiteten Schluessel. Sie erscheint deshalb als "nur im Journal" und
 *   nicht als das, was sie ist — ein Eintrag, den erst das Journal
 *   abrechenbar macht.
 * - Zwei Zeilen mit derselben id und demselben `serviceId` werden zu einem
 *   Paar zusammengefasst (Anzahl + Summe). Vertauschte Mengen INNERHALB eines
 *   solchen Paares faellt der Vergleich nicht auf; fuer die Zuordnung sind sie
 *   ohnehin nicht unterscheidbar, weil `allocations.entryId` beide meint.
 * - Ein Zeitraum wird nur gemessen, wo ein ausgestellter Beleg ihn behauptet
 *   (`perioden`, siehe oben). Einen kuenftigen Beleg ueber einen anderen
 *   Zeitraum sieht dieser Lauf nicht — das misst der Schatten am Tor
 *   (allocationGateShadow.ts) beim Ausstellen.
 * - Der Vergleich sagt nichts darueber, welcher Weg RECHT hat. Er sagt nur,
 *   dass sie auseinanderlaufen.
 *
 * Rein und deterministisch: kein Supabase, kein Date.now(), kein Zufall.
 * Zweimal derselbe Eingang ergibt zeichengleich dasselbe Ergebnis.
 */

/** Wie in allocationLogic: darunter ist eine Menge Rundungsrauschen. */
const EPS = 0.0005;

export interface AllocationShadowInput {
    projectId: string;
    projectNumber?: string | null;
    /** Das Protokoll aus dem Projekt-JSON — die heute gueltige Quelle. */
    jsonLog: ServiceTransaction[];
    /** Die Journalzeilen desselben Projekts, wie sie service_journal fuehrt. */
    journal: ServiceJournalRow[];
    /** Die Belege des Projekts, fuer die bereits vergebenen Zuordnungen. */
    invoices: Array<Pick<Invoice, 'lines'>>;
    /**
     * Optional: die Leistungszeitraeume der AUSGESTELLTEN Belege des Projekts
     * (S5a Stufe 2). Ohne sie bleibt der Vergleich, wie er war.
     */
    perioden?: Array<{ from?: string; to?: string; invoiceNumber?: string }>;
}

export interface AllocationShadowDiff {
    /** Worin sich die beiden Rechnungen unterscheiden. */
    kind:
        | 'eintrag_fehlt_im_journal'
        | 'eintrag_nur_im_journal'
        | 'menge_weicht_ab'
        | 'offene_menge_weicht_ab'
        | 'reihenfolge_weicht_ab'
        | 'zeitraum_menge_weicht_ab';
    entryId?: string;
    serviceId?: string;
    /** Nur bei `zeitraum_menge_weicht_ab`: der Zeitraum, `von..bis`. */
    zeitraum?: string;
    /** Wert aus dem JSON-Weg bzw. aus dem Journal-Weg, wo eine Zahl gemeint ist. */
    json?: number | string | null;
    journal?: number | string | null;
    /** Ein Satz, der die Abweichung fuer einen Menschen benennt. */
    text: string;
}

/** Zeilen, die als ERWARTETE Abweichung nicht einzeln gemeldet werden. */
export interface AllocationShadowFiltered {
    /**
     * Journalzeilen mit `voided_at`. Sie sind gewollt aus dem Protokoll heraus
     * und erzeugen keinen Befund.
     */
    zurueckgenommen: number;
    /**
     * Abrechenbare JSON-Zeilen, die nie ins Journal koennen
     * (`isJournalMappable`). Sie erzeugen keinen Einzelbefund, halten `gleich`
     * aber auf false.
     */
    nichtAbbildbar: number;
}

export interface AllocationShadowResult {
    projectId: string;
    projectNumber?: string | null;
    /**
     * true, wenn beide Wege in allem uebereinstimmen.
     *
     * Ausdruecklich auch dann false, wenn es KEINEN Einzelbefund gibt, aber
     * Zeilen als erwartete Klasse herausgefiltert wurden — sonst behauptete
     * das Ergebnis eine Gleichheit, die der Filter erzeugt hat.
     */
    gleich: boolean;
    /** Zahl der abrechenbaren Eintraege je Weg — die grobe Kennzahl. */
    zaehlerJson: number;
    zaehlerJournal: number;
    /** Summe der offenen Mengen je Weg. */
    offenJson: number;
    offenJournal: number;
    diffs: AllocationShadowDiff[];
    /** Was als erwartete Klasse nicht einzeln gemeldet wurde. */
    gefiltert: AllocationShadowFiltered;
    /**
     * Wie viele verschiedene Belegzeitraeume verglichen wurden — 0 ohne
     * `perioden`. Damit ein Lauf, der keine Zeitraeume bekam, nicht nach
     * "Zeitraeume deckungsgleich" aussieht.
     */
    zeitraeume: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Bausteine
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Trennzeichen des Vergleichsschluessels. Ein Nullbyte, weil weder eine uuid
 * noch eine Leistungskennung es enthalten kann — ein Bindestrich koennte
 * zwei verschiedene Paare auf denselben Schluessel abbilden.
 *
 * Als ESCAPE geschrieben und nicht als echtes Zeichen: eine Datei mit einem
 * Nullbyte gilt fuer grep und ripgrep als Binaerdatei und wird bei jeder Suche
 * nur noch als „Binary file matches" gemeldet.
 */
const TRENNER = '\u0000';

const schluessel = (serviceId: string, entryId: string): string =>
    `${serviceId}${TRENNER}${entryId}`;

/**
 * Eine Zahl in stabiler Textform — nie ueber das Gebietsschema. Exportiert fuer
 * den Schatten am Tor (allocationGateShadow), damit beide Messungen Mengen
 * gleich schreiben.
 */
export const zahl = (n: number): string => {
    // Nie werfen: eine Menge, die als Text ankommt, haette kein `toFixed`, und
    // ein Wurf hier nahm dem ganzen Projekt seinen Nachtvergleich (Codex-Review
    // S5a Stufe 2, 02.10.2026).
    const x = Number(n);
    return Number.isFinite(x) ? String(Number(x.toFixed(3))) : String(n);
};

/**
 * Das Protokoll mit dem BERLINER TAG als Datum — fuer jede Zeitraumfrage im
 * Schattenvergleich, auf BEIDEN Seiten (S5a, Betreiber-Entscheid 1 vom
 * 02.10.2026: das Zuordnungsdatum ist der Berliner Tag).
 *
 * Warum ueberhaupt: `loggedQuantityInPeriod` schneidet den Tag per `asDay` aus
 * dem TEXT. Das JSON fuehrt UTC-Zeitstempel (`…T23:30:00.000Z` ist in Berlin
 * schon der naechste Tag), der Journal-Weg seit V3 die Berliner Wanduhrzeit.
 * Ohne Angleichung laege jede Leistung zwischen 22/23 Uhr UTC und Mitternacht
 * auf den beiden Seiten in verschiedenen Zeitraeumen, und der Vergleich
 * meldete die Zeitzone statt einer Abweichung. `buchungstag` ist dieselbe
 * Regel wie die Journalspalte `entry_day`.
 *
 * Nur das Datum wird ersetzt, und nur in einer KOPIE: die Zeile behaelt id,
 * Menge und Buchungsreferenz, also alles, woran `loggedQuantityInPeriod`
 * Buchungen von Leistung unterscheidet. Ein unlesbares Datum bleibt roh stehen
 * — dieselbe Antwort wie bisher.
 */
export const aufBerlinerTag = (log: ServiceTransaction[] | null | undefined): ServiceTransaction[] =>
    (log ?? []).map((e) => {
        const tag = buchungstag(e?.date);
        return e && tag ? { ...e, date: tag } : e;
    });

/**
 * Die im Zeitraum protokollierte Menge je Leistung — `loggedQuantityInPeriod`
 * selbst, nur auf dem Berliner Tag (`aufBerlinerTag`). Mit `invoices` zaehlt
 * wie dort nur der noch OFFENE Anteil eines Eintrags.
 */
export const zeitraumMengen = (
    log: ServiceTransaction[] | null | undefined,
    zeitraum: { from?: string; to?: string },
    invoices?: Array<Pick<Invoice, 'lines'>>,
): Map<string, number> => loggedQuantityInPeriod(aufBerlinerTag(log), zeitraum, invoices);

/** Das, was eine Seite je (Leistung, Eintrag) beitraegt. */
interface Posten {
    serviceId: string;
    entryId: string;
    /** Wie viele Protokollzeilen dieses Paar tragen (Dubletten). */
    anzahl: number;
    /** Ihre Mengensumme. */
    menge: number;
}

const zusammenfassen = <T extends AllocatableEntry>(
    entries: T[],
    wert: (e: T) => number,
): Map<string, Posten> => {
    const out = new Map<string, Posten>();
    for (const e of entries) {
        const k = schluessel(e.serviceId, e.id);
        const vorhanden = out.get(k);
        if (vorhanden) {
            vorhanden.anzahl += 1;
            vorhanden.menge += wert(e);
        } else {
            out.set(k, { serviceId: e.serviceId, entryId: e.id, anzahl: 1, menge: wert(e) });
        }
    }
    return out;
};

/** Alle Leistungen, unter denen eine id auf dieser Seite auftaucht. */
const leistungenJeId = (entries: AllocatableEntry[]): Map<string, string[]> => {
    const out = new Map<string, string[]>();
    for (const e of entries) {
        const liste = out.get(e.id);
        if (!liste) out.set(e.id, [e.serviceId]);
        else if (!liste.includes(e.serviceId)) liste.push(e.serviceId);
    }
    for (const liste of out.values()) liste.sort();
    return out;
};

/**
 * Der Sortierschluessel einer Zeile innerhalb ihrer Leistung — die Groesse,
 * nach der `orderForPeriod` OHNE Zeitraum ordnet. Undatiertes ans Ende, exakt
 * wie dort. Als GRUPPENschluessel gefasst (`gruppenschluessel`): gleich bis
 * auf die doppelte Herbststunde.
 */
const sortierschluessel = (entries: AllocatableEntry[]): Map<string, string> => {
    const out = new Map<string, string>();
    // Die erste Zeile je id gewinnt: bei einer Dublette mit abweichendem Datum
    // waere jede Wahl willkuerlich, und eine feste Regel haelt das Ergebnis
    // deterministisch.
    for (const e of entries) if (!out.has(e.id)) out.set(e.id, gruppenschluessel(e.date ?? '9999-99-99'));
    return out;
};

/**
 * Eine Probezeile fuer `planAllocations` — sie fordert die volle offene Menge
 * an, damit die Funktion die GANZE Reihenfolge dieser Leistung durchlaeuft.
 *
 * Beide Seiten bekommen dieselbe geforderte Menge (das Maximum beider Wege).
 * Waere es je Seite die eigene Summe, endete der kuerzere Weg frueher und der
 * Reihenfolgevergleich meldete einen Unterschied, der nur eine Mengendifferenz
 * ist — die an anderer Stelle bereits steht.
 */
const probezeile = (serviceId: string, menge: number): InvoiceLine => ({
    id: `shadow-${serviceId}`,
    kind: 'SERVICE',
    title: '',
    serviceId,
    quantity: menge,
    taxRateBp: 0,
    netCents: 0,
});

/**
 * Die Reihenfolge einer Leistung, gruppenweise normalisiert.
 *
 * Gleicher Sortierschluessel = Unentschieden: die Ordnung darin entschied
 * bis zum 03.10.2026 allein die Arrayposition und war zwischen JSON und
 * Journal systematisch verschieden, ohne dass sich an der Zuordnung etwas
 * aenderte. Seither entscheidet die id; das Unentschieden bleibt trotzdem
 * unbewertet.
 */
/**
 * Ein Zeitpunkt in EINER Schreibweise — BERLINER WANDUHRZEIT, beide Seiten.
 *
 * ─── Warum ueberhaupt kanonisiert wird ─────────────────────────────────────
 * `entry_at` ist in Nr. 59 `timestamptz`, und PostgREST gibt solche Werte
 * normalisiert zurueck (`+00:00`, keine Millisekunden). Das JSON fuehrt die
 * Originalschreibweisen. Ginge der Rohtext in den Reihenfolgevergleich, waeren
 * die beiden Seiten praktisch nie gleich.
 *
 * ─── Warum BERLIN und nicht UTC ────────────────────────────────────────────
 * Die Bestandsuebernahme (Nr. 60) verankert einen Wert OHNE Zone — und ein
 * reines Tagesdatum — ausdruecklich in `Europe/Berlin` und nicht in der
 * Serverzeitzone; nur so sagen `entry_at` und `entry_day` dasselbe. Aus
 * `2026-05-10` wird dort also `2026-05-09T22:00:00Z`.
 *
 * Eine Kanonisierung nach UTC verschob deshalb JEDE solche Zeile um den
 * Berliner Zonenversatz gegen ihre JSON-Fassung. Der erste Messlauf ueber die
 * Flotte hat genau das gezeigt: 1141 Reihenfolge-Meldungen auf 467 von 538
 * Projekten — und nicht eine einzige Mengen- oder Eintragsabweichung. Ein
 * reiner Schreibweisen-Unterschied, der wie ein Befund aussah.
 *
 * Beide Seiten werden deshalb auf die WANDUHRZEIT in Europe/Berlin gebracht:
 * dieselbe Regel, die `entryDay` (serviceJournal.ts) und Nr. 60 anwenden.
 *
 * ─── Der zonenlose Wert geht NICHT durch ein Date-Objekt ───────────────────
 * `new Date('2026-05-05T08:30:00')` liest JavaScript als GERAETEZEIT. Ein
 * solcher Wert IST bereits die Wanduhrzeit des Betriebs — sein Text bleibt
 * deshalb unangetastet, genau wie in `entryDay`.
 */
const HAT_ZONE = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i;
const NUR_TAG = /^\d{4}-\d{2}-\d{2}$/;

/** Die feste Zone der Anwendung — nie die des Geraets, nie die des Servers. */
const BERLIN = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
});

/** `YYYY-MM-DDTHH:MM:SS` in Berliner Zeit — nie ueber das Gebietsschema. */
const berlinerWanduhr = (d: Date): string => {
    const teile = BERLIN.formatToParts(d);
    const teil = (typ: string) => teile.find((t) => t.type === typ)?.value ?? '';
    // 24 Uhr statt 00 Uhr kommt bei hour12:false in manchen Laufzeiten vor.
    const stunde = teil('hour') === '24' ? '00' : teil('hour');
    return `${teil('year')}-${teil('month')}-${teil('day')}T${stunde}:${teil('minute')}:${teil('second')}`;
};

export const kanonischerZeitpunkt = (wert: string): string => {
    const roh = String(wert ?? '').trim();
    if (!roh) return roh;
    // ZUERST der reine Tag, und das ist keine Geschmacksfrage: `2026-05-10`
    // endet auf `-10` und erfuellt damit das Muster eines Zonenversatzes.
    // Andersherum geprueft liefe jedes Tagesdatum durch die Zonenrechnung und
    // kaeme um zwei Stunden verschoben heraus. `entryDay` in serviceJournal.ts
    // prueft aus demselben Grund in dieser Reihenfolge.
    if (NUR_TAG.test(roh)) return `${roh}T00:00:00`;
    if (HAT_ZONE.test(roh)) {
        const d = new Date(roh);
        if (!isNaN(d.getTime())) return berlinerWanduhr(d);
        return roh;
    }
    const ohneBruch = roh.replace(' ', 'T').replace(/\.\d+$/, '');
    return ohneBruch.length >= 19 ? ohneBruch.slice(0, 19) : ohneBruch;
};

/** `YYYY-MM-DDTHH:MM:SS` — die Form, die `kanonischerZeitpunkt` liefert. */
const WANDUHR = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/;

/**
 * Liegt diese Berliner Wanduhrzeit in der DOPPELTEN Herbststunde — gibt es
 * sie also zweimal, einmal in Sommer- und einmal in Winterzeit? Geprueft an
 * beiden moeglichen Zeitpunkten (Versatz eine bzw. zwei Stunden): nur dort
 * zeigen BEIDE dieselbe Wanduhr.
 *
 * Vorher ein billiger Vorfilter, damit der Nachtlauf nicht fuer jede Zeile
 * zweimal `Intl` ruft: die doppelte Stunde ist seit 1980 immer 02:00–03:00
 * Ortszeit am letzten Sonntag im September (bis 1995) bzw. Oktober (seit
 * 1996) — also Monat 9 oder 10, Tag ab 24, Stunde 02. Alles andere ist es
 * nie. Exportiert nur fuer den Test, der den Vorfilter gegen die volle
 * Pruefung haelt.
 */
export const inDoppelterStunde = (wanduhr: string): boolean => {
    const m = WANDUHR.exec(wanduhr);
    if (!m) return false;
    const monat = Number(m[2]);
    if (m[4] !== '02' || (monat !== 9 && monat !== 10) || Number(m[3]) < 24) return false;
    const wand = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6]));
    if (isNaN(wand)) return false;
    return berlinerWanduhr(new Date(wand - 3_600_000)) === wanduhr
        && berlinerWanduhr(new Date(wand - 7_200_000)) === wanduhr;
};

/**
 * Der Gruppenschluessel des Reihenfolgevergleichs: der rohe Sortierschluessel
 * — AUSSER in der doppelten Herbststunde (letzter Oktobersonntag, 02:00–03:00
 * Ortszeit). Dort fasst er die ganze Stunde zu EINEM Unentschieden zusammen.
 *
 * ─── Warum (V3 vor S5a) ────────────────────────────────────────────────────
 * Der Journal-Weg liefert `date` seit V3 als Berliner Wanduhrzeit mit Versatz
 * (`journalToServiceTransactions`, `berlinerZeitstempel`), damit `asDay` den
 * Berliner Tag liest. `orderForPeriod` sortierte damals den TEXT, und der ist
 * chronologisch bis auf genau diese Stunde: `02:15+01:00` (01:15Z) steht vor
 * `02:59+02:00` (00:59Z). Das JSON (UTC-Text) ordnete dort andersherum, und
 * der Vergleich meldete eine Reihenfolgeabweichung, die vor V3 nicht da war.
 * Seit dem 03.10.2026 sortieren beide Wege nach der Berliner Wanduhr und
 * ordnen auch diese Stunde gleich; das Zusammenfassen bleibt als Schutz,
 * schadet aber nicht (siehe unten).
 * Fuer die Zuordnung ist sie belanglos — dieselbe Stunde desselben Tages,
 * also derselbe Tag, derselbe Monat, dasselbe Geld; genau das Unentschieden,
 * das der Vergleich ohnehin nicht bewertet.
 *
 * Nur diese Stunde: ueberall sonst bleibt der ROHE Schluessel, damit jede
 * Meldung, die es vor V3 gab, genau so bleibt. Auf beiden Seiten liegt die
 * Stunde als geschlossener Block (die Reihenfolge der Tage ist dieselbe),
 * das Zusammenfassen kann deshalb keinen neuen Unterschied erzeugen — nur
 * einen innerhalb dieser Stunde verschlucken.
 */
const gruppenschluessel = (roh: string): string => {
    const wanduhr = kanonischerZeitpunkt(roh);
    return inDoppelterStunde(wanduhr) ? `${wanduhr.slice(0, 13)}:xx:xx` : roh;
};

const gruppiert = (ids: string[], schluesselVon: (id: string) => string): string => {
    const gruppen: string[] = [];
    let i = 0;
    while (i < ids.length) {
        const k = schluesselVon(ids[i]);
        const block: string[] = [];
        while (i < ids.length && schluesselVon(ids[i]) === k) {
            block.push(ids[i]);
            i += 1;
        }
        block.sort();
        gruppen.push(`${kanonischerZeitpunkt(k)}[${block.join(',')}]`);
    }
    return gruppen.join(' > ');
};

/** Rang je Abweichungsart — nur fuer die stabile Ausgabereihenfolge. */
const RANG: Record<AllocationShadowDiff['kind'], number> = {
    eintrag_fehlt_im_journal: 0,
    eintrag_nur_im_journal: 1,
    menge_weicht_ab: 2,
    offene_menge_weicht_ab: 3,
    reihenfolge_weicht_ab: 4,
    zeitraum_menge_weicht_ab: 5,
};

/** Ein Belegzeitraum, einmal je `von..bis`, mit den Belegen, die ihn nennen. */
interface Zeitraum {
    from?: string;
    to?: string;
    schluessel: string;
    belege: string[];
}

/**
 * Die mitgegebenen Zeitraeume, je `von..bis` EINMAL und sortiert. Storno und
 * Ursprungsbeleg nennen denselben Zeitraum; doppelt verglichen stuende
 * dieselbe Abweichung zweimal da. Ohne Anfang UND Ende ist es kein Zeitraum.
 */
const zeitraeumeAus = (perioden: AllocationShadowInput['perioden']): Zeitraum[] => {
    const out = new Map<string, Zeitraum>();
    for (const p of perioden ?? []) {
        const from = asDay(p?.from || undefined);
        const to = asDay(p?.to || undefined);
        if (!from && !to) continue;
        const k = `${from ?? '…'}..${to ?? '…'}`;
        const vorhanden = out.get(k);
        const nummer = p?.invoiceNumber ? String(p.invoiceNumber) : '';
        if (vorhanden) {
            if (nummer && !vorhanden.belege.includes(nummer)) vorhanden.belege.push(nummer);
        } else {
            out.set(k, { from, to, schluessel: k, belege: nummer ? [nummer] : [] });
        }
    }
    return [...out.values()].sort((a, b) => a.schluessel.localeCompare(b.schluessel));
};

// ═══════════════════════════════════════════════════════════════════════════
// Der Vergleich
// ═══════════════════════════════════════════════════════════════════════════

export const compareAllocation = (input: AllocationShadowInput): AllocationShadowResult => {
    const jsonLog = input.jsonLog ?? [];
    const journalRows = input.journal ?? [];

    // Der Journal-Weg macht nichts Eigenes: dieselbe Rueckabbildung, die
    // `getServiceLog` spaeter benutzt, und danach dieselben Funktionen.
    const journalLog = journalToServiceTransactions(journalRows);

    // Die bereits vergebenen Zuordnungen sind fuer BEIDE Wege dieselben: sie
    // stehen im Beleg und nicht im Protokoll. Eine Abweichung der offenen
    // Menge kommt deshalb immer aus dem Protokoll — genau das soll sie.
    const vergeben = allocatedByEntry(input.invoices ?? []);

    // Kennzahlen: der rohe Stand jedes Weges, ungefiltert.
    const jsonAbrechenbar = allocatableEntries(jsonLog);
    const journalAbrechenbar = allocatableEntries(journalLog);
    const jsonOffen = openEntries(jsonLog, vergeben);
    const journalOffen = openEntries(journalLog, vergeben);

    // Fuer den VERGLEICH bleiben die nicht abbildbaren JSON-Zeilen draussen —
    // sie koennen im Journal nie stehen, und jede von ihnen erzeugte sonst
    // einen Befund, der nie verschwindet. Gezaehlt werden sie unten.
    const jsonLogAbbildbar = jsonLog.filter((e) => isJournalMappable(e));
    const jsonAbrechenbarAbbildbar = allocatableEntries(jsonLogAbbildbar);
    const jsonOffenAbbildbar = openEntries(jsonLogAbbildbar, vergeben);

    const gefiltert: AllocationShadowFiltered = {
        zurueckgenommen: journalRows.filter((r) => !!r?.voided_at).length,
        nichtAbbildbar: jsonAbrechenbar.length - jsonAbrechenbarAbbildbar.length,
    };

    const diffs: AllocationShadowDiff[] = [];

    // ── Bestand je (Leistung, Eintrag) ────────────────────────────────────
    const jsonMenge = zusammenfassen(jsonAbrechenbarAbbildbar, (e) => e.quantity);
    const journalMenge = zusammenfassen(journalAbrechenbar, (e) => e.quantity);
    const jsonOffenJe = zusammenfassen(jsonOffenAbbildbar, (e) => e.openQty);
    const journalOffenJe = zusammenfassen(journalOffen, (e) => e.openQty);

    const jsonLeistungen = leistungenJeId(jsonAbrechenbarAbbildbar);
    const journalLeistungen = leistungenJeId(journalAbrechenbar);

    const alleSchluessel = [...new Set([...jsonMenge.keys(), ...journalMenge.keys()])].sort();
    const gemeinsam = new Set<string>();

    for (const k of alleSchluessel) {
        const a = jsonMenge.get(k);
        const b = journalMenge.get(k);
        const serviceId = (a ?? b)!.serviceId;
        const entryId = (a ?? b)!.entryId;

        if (!b) {
            // Steht dieselbe id im Journal unter einer ANDEREN Leistung, ist es
            // kein Verlust, sondern ein Umzug — und der ist die schwerere
            // Aussage: die Menge landet auf einer anderen LV-Position.
            const anderswo = (journalLeistungen.get(entryId) ?? []).filter((s) => s !== serviceId);
            diffs.push({
                kind: 'eintrag_fehlt_im_journal',
                entryId,
                serviceId,
                json: a!.anzahl,
                journal: 0,
                text: anderswo.length
                    ? `Eintrag ${entryId} gehoert im JSON zu Leistung ${serviceId}, im Journal zu ${anderswo.join(', ')} — die Menge wuerde an einer anderen LV-Position abgerechnet.`
                    : `Eintrag ${entryId} (Leistung ${serviceId}, ${zahl(a!.menge)}) ist abrechenbar, steht aber nicht im Journal — der Journal-Weg forderte ihn nie.`,
            });
            continue;
        }

        if (!a) {
            const anderswo = (jsonLeistungen.get(entryId) ?? []).filter((s) => s !== serviceId);
            diffs.push({
                kind: 'eintrag_nur_im_journal',
                entryId,
                serviceId,
                json: 0,
                journal: b.anzahl,
                text: anderswo.length
                    ? `Eintrag ${entryId} gehoert im Journal zu Leistung ${serviceId}, im JSON zu ${anderswo.join(', ')} — die Menge wuerde an einer anderen LV-Position abgerechnet.`
                    : `Eintrag ${entryId} (Leistung ${serviceId}, ${zahl(b.menge)}) steht nur im Journal — der Journal-Weg forderte ihn zusaetzlich.`,
            });
            continue;
        }

        gemeinsam.add(k);

        if (a.anzahl !== b.anzahl) {
            diffs.push({
                kind: a.anzahl > b.anzahl ? 'eintrag_fehlt_im_journal' : 'eintrag_nur_im_journal',
                entryId,
                serviceId,
                json: a.anzahl,
                journal: b.anzahl,
                text: `Eintrag ${entryId} (Leistung ${serviceId}) steht im JSON ${a.anzahl}-mal, im Journal ${b.anzahl}-mal.`,
            });
        }

        const mengeAb = Math.abs(a.menge - b.menge) > EPS;
        if (mengeAb) {
            diffs.push({
                kind: 'menge_weicht_ab',
                entryId,
                serviceId,
                json: a.menge,
                journal: b.menge,
                text: `Eintrag ${entryId} (Leistung ${serviceId}): erbrachte Menge ${zahl(a.menge)} im JSON gegen ${zahl(b.menge)} im Journal.`,
            });
        }

        const offenA = jsonOffenJe.get(k)?.menge ?? 0;
        const offenB = journalOffenJe.get(k)?.menge ?? 0;
        const offenDelta = offenA - offenB;
        // Die offene Menge nur dann eigens melden, wenn sie NICHT bloss die
        // Folge der schon gemeldeten Mengendifferenz ist: sonst stuenden fuer
        // eine Ursache zwei Befunde, und die Liste wird unlesbar.
        if (Math.abs(offenDelta) > EPS && Math.abs(offenDelta - (a.menge - b.menge)) > EPS) {
            diffs.push({
                kind: 'offene_menge_weicht_ab',
                entryId,
                serviceId,
                json: offenA,
                journal: offenB,
                text: `Eintrag ${entryId} (Leistung ${serviceId}): offen ${zahl(offenA)} im JSON gegen ${zahl(offenB)} im Journal.`,
            });
        }
    }

    // ── Reihenfolge je Leistung ───────────────────────────────────────────
    // Nur die Eintraege, die es auf BEIDEN Wegen gibt. Ein fehlender oder
    // zusaetzlicher Eintrag steht bereits oben; er duerfte die Reihenfolge
    // sonst ein zweites Mal verruecken und jede Leistung laut machen, in der
    // er vorkommt.
    const jsonTag = sortierschluessel(jsonAbrechenbarAbbildbar);
    const journalTag = sortierschluessel(journalAbrechenbar);

    const offenJeLeistung = (posten: Map<string, Posten>): Map<string, number> => {
        const out = new Map<string, number>();
        for (const p of posten.values()) {
            out.set(p.serviceId, (out.get(p.serviceId) ?? 0) + p.menge);
        }
        return out;
    };
    const bedarfJson = offenJeLeistung(jsonOffenJe);
    const bedarfJournal = offenJeLeistung(journalOffenJe);

    const leistungen = [...new Set([...bedarfJson.keys(), ...bedarfJournal.keys()])].sort();
    for (const serviceId of leistungen) {
        const bedarf = Math.max(bedarfJson.get(serviceId) ?? 0, bedarfJournal.get(serviceId) ?? 0);
        if (bedarf <= EPS) continue;

        // Der Ernstfall selbst, zweimal: dieselbe Funktion, dieselbe geforderte
        // Menge, nur ein anderes Protokoll.
        const [zeileJson] = planAllocations(
            [probezeile(serviceId, bedarf)],
            jsonLogAbbildbar,
            vergeben,
        );
        const [zeileJournal] = planAllocations([probezeile(serviceId, bedarf)], journalLog, vergeben);

        const nurGemeinsam = (ids: string[]): string[] =>
            ids.filter((id) => gemeinsam.has(schluessel(serviceId, id)));

        const folgeJson = gruppiert(
            nurGemeinsam((zeileJson?.allocations ?? []).map((x) => x.entryId)),
            (id) => jsonTag.get(id) ?? '9999-99-99',
        );
        const folgeJournal = gruppiert(
            nurGemeinsam((zeileJournal?.allocations ?? []).map((x) => x.entryId)),
            (id) => journalTag.get(id) ?? '9999-99-99',
        );

        if (folgeJson !== folgeJournal) {
            diffs.push({
                kind: 'reihenfolge_weicht_ab',
                serviceId,
                json: folgeJson,
                journal: folgeJournal,
                text: `Leistung ${serviceId}: die Zuordnung greift in anderer Reihenfolge zu — JSON "${folgeJson}", Journal "${folgeJournal}".`,
            });
        }
    }

    // ── Die Zeitraeume der ausgestellten Belege (S5a Stufe 2) ─────────────
    // Bruttomenge je Zeitraum und Leistung, beidseitig auf dem Berliner Tag;
    // warum ohne Belegabzug, steht im Kopf. Die JSON-Seite ohne die nicht
    // abbildbaren Zeilen, aus demselben Grund wie oben: sie sind gezaehlt und
    // halten `gleich` ohnehin auf false.
    const zeitraeume = zeitraeumeAus(input.perioden);
    for (const z of zeitraeume) {
        const mengenJson = zeitraumMengen(jsonLogAbbildbar, z);
        const mengenJournal = zeitraumMengen(journalLog, z);
        const belege = z.belege.length
            ? ` (${z.belege.slice(0, 3).join(', ')}${z.belege.length > 3 ? ', …' : ''})`
            : '';
        for (const serviceId of [...new Set([...mengenJson.keys(), ...mengenJournal.keys()])].sort()) {
            const a = mengenJson.get(serviceId) ?? 0;
            const b = mengenJournal.get(serviceId) ?? 0;
            if (Math.abs(a - b) <= EPS) continue;
            diffs.push({
                kind: 'zeitraum_menge_weicht_ab',
                serviceId,
                zeitraum: z.schluessel,
                json: a,
                journal: b,
                text: `Leistung ${serviceId}, Zeitraum ${z.schluessel}${belege}: protokolliert ${zahl(a)} im JSON gegen ${zahl(b)} im Journal (beidseitig Berliner Tag).`,
            });
        }
    }

    diffs.sort(
        (x, y) =>
            RANG[x.kind] - RANG[y.kind] ||
            (x.serviceId ?? '').localeCompare(y.serviceId ?? '') ||
            (x.entryId ?? '').localeCompare(y.entryId ?? '') ||
            (x.zeitraum ?? '').localeCompare(y.zeitraum ?? ''),
    );

    const summe = (entries: OpenEntry[]): number =>
        Number(entries.reduce((s, e) => s + e.openQty, 0).toFixed(3));

    return {
        projectId: input.projectId,
        projectNumber: input.projectNumber,
        gleich: diffs.length === 0 && gefiltert.nichtAbbildbar === 0,
        zaehlerJson: jsonAbrechenbar.length,
        zaehlerJournal: journalAbrechenbar.length,
        offenJson: summe(jsonOffen),
        offenJournal: summe(journalOffen),
        diffs,
        gefiltert,
        zeitraeume: zeitraeume.length,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// Die Protokollzeile
// ═══════════════════════════════════════════════════════════════════════════

const BEZEICHNUNG: Record<AllocationShadowDiff['kind'], string> = {
    eintrag_fehlt_im_journal: 'fehlt im Journal',
    eintrag_nur_im_journal: 'nur im Journal',
    menge_weicht_ab: 'Menge weicht ab',
    offene_menge_weicht_ab: 'offene Menge weicht ab',
    reihenfolge_weicht_ab: 'Reihenfolge weicht ab',
    zeitraum_menge_weicht_ab: 'Zeitraummenge weicht ab',
};

/**
 * Eine Zeile fuer das Lauf-Protokoll; leer, wenn nichts abweicht.
 *
 * Die zurueckgenommenen Zeilen erscheinen nur, wenn ohnehin etwas zu melden
 * ist: sie sind keine Abweichung, sondern eine gewollte Ruecknahme. Die nicht
 * abbildbaren erscheinen IMMER — sie sind eine Abweichung, die nur nicht
 * einzeln aufgefuehrt wird, und wer sie nicht sieht, misst am Filter vorbei.
 */
export const describeShadowResult = (r: AllocationShadowResult): string => {
    if (r.gleich) return '';

    const teile: string[] = [];
    for (const kind of Object.keys(RANG) as Array<AllocationShadowDiff['kind']>) {
        const n = r.diffs.filter((d) => d.kind === kind).length;
        if (n > 0) teile.push(`${n}x ${BEZEICHNUNG[kind]}`);
    }
    if (r.gefiltert.nichtAbbildbar > 0) {
        teile.push(`${r.gefiltert.nichtAbbildbar} nicht abbildbar (koennen nie im Journal stehen)`);
    }
    if (r.gefiltert.zurueckgenommen > 0) {
        teile.push(`${r.gefiltert.zurueckgenommen} zurueckgenommen`);
    }

    const name = r.projectNumber ? `${r.projectNumber} (${r.projectId})` : r.projectId;
    return (
        `${name}: ${r.zaehlerJson}/${r.zaehlerJournal} abrechenbar, ` +
        `offen ${zahl(r.offenJson)}/${zahl(r.offenJournal)} — ${teile.join(', ')}`
    );
};
